import {
  MEDIA_QUERY_DEVICE_SIZE,
  WIDTH_DESCRIPTORS,
} from '@/app/components/media/image/constants';
import type {
  ImageLoadingType,
  ImageOptions,
  ImageRenderOption,
  ImageSource,
} from '@/app/components/media/image/types';
import { isPng, isUnprocessableImageUrl } from '@/app/utils/image';
import { isImageKitUrl } from '@/app/utils/imagekit';

/** Build ImageKit tr param string (e.g. tr=w-300,h-200,q-75) */
const buildImageKitTr = (
  options: ImageOptions,
  as: ImageRenderOption
): string => {
  const {
    width,
    height,
    crop,
    fit,
    focus,
    blur,
    trim,
    quality = 75,
    dpr = 1,
  } = options;
  const parts: string[] = [];
  if (width) parts.push(`w-${width}`);
  if (height) parts.push(`h-${height}`);
  if (crop) parts.push(`ar-${String(crop).replace(':', '-')}`);
  if (trim) parts.push(`t-${trim}`);
  if (blur) parts.push(`bl-${blur}`);

  switch (fit) {
    case 'bounds':
      parts.push('c-at_max');
      break;
    case 'crop':
      parts.push('c-at_least');
      break;
  }

  if (focus) {
    parts.push(`fo-${focus}`);
  }

  parts.push(`q-${quality}`);
  if (as !== 'sizes') {
    const dprVal =
      width && width >= MEDIA_QUERY_DEVICE_SIZE.desktop_xl ? 1.5 : dpr;
    if (dprVal > 1) parts.push(`dpr-${dprVal}`);
  }
  return parts.join(',');
};

export const getImageQueryParams = (
  src: string = '',
  options: ImageOptions = {},
  as: ImageRenderOption
): string => {
  const {
    width,
    height,
    crop,
    fit,
    trim,
    blur,
    quality = 75,
    dpr = 1,
    autoFormat = 'webp',
  } = options;

  if (isUnprocessableImageUrl(src)) {
    return src;
  }

  if (isImageKitUrl(src)) {
    const tr = buildImageKitTr(options, as);
    const sep = src.includes('?') ? '&' : '?';
    return `${src}${sep}tr=${encodeURIComponent(tr)}`;
  }

  const imageParams = new URLSearchParams({
    format: isPng(src) ? 'png' : 'pjpg',
    auto: autoFormat,
    quality: `${quality}`,
  });
  if (width) imageParams.set('width', `${width}`);
  if (height) imageParams.set('height', `${height}`);
  if (crop) imageParams.set('crop', `${crop}`);
  if (fit) imageParams.set('fit', `${fit}`);
  if (trim) imageParams.set('trim', `${trim}`);
  if (blur) imageParams.set('blur', `${blur}`);
  if (as === 'sizes') {
    return `${src}?${imageParams.toString()}`;
  }

  if (width && width >= MEDIA_QUERY_DEVICE_SIZE.desktop_xl) {
    imageParams.set('dpr', '1.5');
  } else if (dpr > 1) {
    imageParams.set('dpr', `${dpr}`);
  }
  return `${src}?${imageParams.toString()}`;
};

export const getImgSrcset = (
  src: string,
  options: ImageOptions = {},
  inView?: boolean,
  loading?: ImageLoadingType
): string | undefined => {
  if (!src || (loading === 'lazy' && !inView) || isUnprocessableImageUrl(src))
    return undefined;

  return WIDTH_DESCRIPTORS.map((width) => {
    const url = getImageQueryParams(src, { ...options, width }, 'sizes');
    return `${url} ${width}w`;
  }).join(', ');
};

export const getSourceSrcset = ({
  srcset,
  options,
  media,
}: ImageSource): string => {
  if (!srcset) return '';

  if (isUnprocessableImageUrl(srcset)) return srcset;

  const srcset1x = getImageQueryParams(
    srcset,
    { ...options, dpr: 1.5 },
    'sources'
  );
  const srcset2x = getImageQueryParams(
    srcset,
    { ...options, dpr: 2 },
    'sources'
  );
  let srcsetString = `${srcset1x} 1x, ${srcset2x} 2x`;

  if (
    media &&
    parseInt(`${media.maxWidth}`) < MEDIA_QUERY_DEVICE_SIZE.mobile_md
  ) {
    const srcset3x = getImageQueryParams(
      srcset,
      { ...options, dpr: 2.5 },
      'sources'
    );
    srcsetString += `, ${srcset3x} 3x`;
  }

  return srcsetString;
};

export const getMediaCondition = (source: ImageSource): string => {
  const { media, srcset } = source;
  if (!media) return '';

  if (!isUnprocessableImageUrl(srcset)) {
    const conditions: string[] = [];
    if (media?.minWidth) conditions.push(`(min-width: ${media.minWidth}px)`);
    if (media?.maxWidth) conditions.push(`(max-width: ${media.maxWidth}px)`);
    if (media?.orientation)
      conditions.push(`(orientation: ${media.orientation})`);
    return conditions.join(' and ');
  }

  if (!!media.device) {
    return media.device === 'desktop'
      ? `(min-width: ${media.minWidth}px)`
      : `(max-width: ${media.maxWidth}px)`;
  }

  // If there's no device information, it will pick minWidth or maxWidth value.
  let result = '';
  if (media?.minWidth) {
    result = `(min-width: ${media.minWidth}px)`;
  }
  // maxWidth has more priority because mobile has more user
  if (media?.maxWidth) {
    result = `(max-width: ${media.maxWidth}px)`;
  }
  return result;
};

export const getAdjustedImageSrc = (
  src: string,
  defaultSrc: string,
  inView: boolean,
  loading: ImageLoadingType
): string => {
  if (!src) return defaultSrc;

  return loading === 'eager' || inView ? src : defaultSrc;
};
